import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  FileText, Printer, RotateCcw, Save, Building2, ClipboardCheck, Download,
} from "lucide-react";

/**
 * PB Form Panel — Postal Bill of Export-II (Form-II) fillable form.
 *
 * Layout mirrors the government PB Form PDF. Selecting a Sender Company
 * auto-fills exporter identity (Name, Address, IEC, GSTIN, State code).
 * Everything else is filled manually. A Print button opens the browser
 * print dialog on a print-friendly A4 stylesheet so the printed page
 * looks identical to the original PDF.
 */

const DRAFT_KEY = "pb_form_draft_v1";

const emptyForm = {
  bill_of_export_no: "",
  bill_of_export_date: "",
  foreign_post_office_code: "",
  // Exporter (auto-filled from company)
  exporter_name: "",
  exporter_address: "",
  iec: "",
  state_code: "",
  gstin: "",
  ad_code: "",
  // Broker (manual)
  broker_licence: "",
  broker_name_address: "",
  // Declaration Yes/No (3 rows)
  decl_meis: "",
  decl_zero_rate: "",
  decl_exempted: "",
  // Consignee (manual)
  consignee_name_address: "",
  country_of_destination: "",
  // Parcel table (single row for MVP — matches PDF sample)
  parcel_description: "PHARMACEUTICAL PRODUCT",
  parcel_hsn: "3004",
  parcel_qty: "",
  parcel_unit: "Pieces",
  parcel_invoice_no: "",
  parcel_invoice_date: "",
  parcel_weight: "AS PER ANNEXURE",
  parcel_tracking_no: "AS PER ANNEXURE",
  parcel_fob: "",
  parcel_currency: "$",
  parcel_exchange_rate: "",
  parcel_amount_inr: "",
  // Tax Invoice / Duty
  tax_hsn: "3004",
  tax_invoice_no_date: "",
  tax_si_no: "",
  tax_value_dollars: "",
  duty_export_rate: "",
  duty_export_amount: "",
  duty_cess_rate: "",
  duty_cess_amount: "",
  duty_igst_rate: "",
  duty_igst_amount: "",
  duty_compensation_rate: "",
  duty_compensation_amount: "",
  lut_bond: "",
  total_duty: "",
  total_cess: "",
};

const deriveStateCode = (gstin) =>
  (gstin || "").trim().slice(0, 2) || "";

const buildAddress = (co) => {
  if (!co) return "";
  return [co.address_line_1, co.address_line_2, co.address_line_3, co.pincode]
    .filter(Boolean).join(", ");
};

const fmtInvDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yy = String(d.getFullYear()).slice(-2);
    return `${dd}-${mm}-${yy}`;
  } catch { return ""; }
};

// Inline "underline" input used throughout the printable PB form.
// Defined at module scope so React does NOT remount it on every keystroke
// (which would cause the input to lose focus after every character).
const PbField = ({ value, onChange, placeholder, className = "", multiline, ...rest }) => (
  multiline ? (
    <textarea
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={3}
      className={`w-full text-xs border-0 border-b border-slate-300 focus:outline-none focus:border-indigo-500 bg-transparent px-1 py-0.5 resize-none ${className}`}
      {...rest}
    />
  ) : (
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full text-xs border-0 border-b border-slate-300 focus:outline-none focus:border-indigo-500 bg-transparent px-1 py-0.5 ${className}`}
      {...rest}
    />
  )
);

const PbFormPanel = ({ user }) => {
  const [companies, setCompanies] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [selectedShipmentId, setSelectedShipmentId] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const printRef = useRef(null);

  // Load companies + shipments + any saved draft
  const loadData = useCallback(async () => {
    try {
      const [coRes, shRes] = await Promise.all([
        axios.get(`${API}/companies`, { headers: getAuthHeader() }),
        axios.get(`${API}/shipments?scope=all`, { headers: getAuthHeader() })
             .catch(() => ({ data: { shipments: [] } })),
      ]);
      const coList = Array.isArray(coRes.data) ? coRes.data : (coRes.data.companies || []);
      setCompanies(coList);
      setShipments(shRes.data.shipments || []);
    } catch (e) {
      toast.error("Failed to load companies");
    }
  }, []);

  useEffect(() => {
    loadData();
    // Restore draft if present
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          setForm(prev => ({ ...prev, ...parsed.form }));
          if (parsed.selectedCompanyId) setSelectedCompanyId(parsed.selectedCompanyId);
          if (parsed.selectedShipmentId) setSelectedShipmentId(parsed.selectedShipmentId);
        }
      }
    } catch { /* ignore */ }
  }, [loadData]);

  const handleCompanyChange = (id) => {
    setSelectedCompanyId(id);
    if (id === "__none__") {
      // Clear auto-filled fields
      setForm(f => ({
        ...f, exporter_name: "", exporter_address: "",
        iec: "", state_code: "", gstin: "",
      }));
      return;
    }
    const co = companies.find(c => c.id === id);
    if (!co) return;
    setForm(f => ({
      ...f,
      exporter_name: co.name || "",
      exporter_address: buildAddress(co),
      iec: co.iec || "",
      gstin: co.gstin || "",
      state_code: deriveStateCode(co.gstin),
    }));
    toast.success(`Exporter details filled from ${co.name}`);
  };

  const handleShipmentChange = (id) => {
    setSelectedShipmentId(id);
    if (id === "__none__") return;
    const sh = shipments.find(s => s.id === id);
    if (!sh) return;

    // If the shipment is linked to a Sender Company, cascade-fill that too
    if (sh.company_id && sh.company_id !== selectedCompanyId) {
      handleCompanyChange(sh.company_id);
    }

    const invNoDate = [sh.invoice_no, fmtInvDate(sh.created_at)].filter(Boolean).join(" / ");
    setForm(f => ({
      ...f,
      parcel_invoice_no: sh.invoice_no || f.parcel_invoice_no,
      parcel_invoice_date: fmtInvDate(sh.created_at) || f.parcel_invoice_date,
      consignee_name_address: sh.customer_name || f.consignee_name_address,
      tax_invoice_no_date: invNoDate || f.tax_invoice_no_date,
    }));
    toast.success(`Shipment ${sh.shipment_no} details filled — tracking / FOB / country still need manual entry`);
  };

  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const saveDraft = () => {
    setSaving(true);
    try {
      localStorage.setItem(DRAFT_KEY,
        JSON.stringify({ form, selectedCompanyId, selectedShipmentId }));
      toast.success("Draft saved locally");
    } finally { setSaving(false); }
  };

  const clearDraft = () => {
    if (!window.confirm("Reset the form and clear the saved draft?")) return;
    localStorage.removeItem(DRAFT_KEY);
    setForm(emptyForm);
    setSelectedCompanyId("");
    setSelectedShipmentId("");
    toast.success("Form reset");
  };

  const doPrint = () => {
    window.print();
  };

  return (
    <div className="p-4 md:p-6 space-y-4 min-h-full bg-slate-50" data-testid="pb-form-panel">
      {/* Toolbar (hidden on print) */}
      <div className="flex items-center justify-between flex-wrap gap-3 print:hidden">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <FileText className="w-7 h-7 text-purple-600" />
            PB Forms — Postal Bill of Export-II
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Select a Sender Company to auto-fill exporter details. Everything else is filled manually.
            Print produces an A4 page matching the government form.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="w-64">
            <Select value={selectedCompanyId || "__none__"} onValueChange={handleCompanyChange}>
              <SelectTrigger data-testid="pb-company-select">
                <SelectValue placeholder="Select Sender Company" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— None —</SelectItem>
                {companies.map(c => (
                  <SelectItem key={c.id} value={c.id} data-testid={`pb-company-option-${c.id}`}>
                    <Building2 className="w-3 h-3 inline mr-1" />
                    {c.name} · {c.gstin}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-64">
            <Select value={selectedShipmentId || "__none__"} onValueChange={handleShipmentChange}>
              <SelectTrigger data-testid="pb-shipment-select">
                <SelectValue placeholder="Autofill from Shipment (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— None —</SelectItem>
                {shipments.map(s => (
                  <SelectItem key={s.id} value={s.id} data-testid={`pb-shipment-option-${s.id}`}>
                    <ClipboardCheck className="w-3 h-3 inline mr-1" />
                    {s.shipment_no}
                    {s.invoice_no ? ` · ${s.invoice_no}` : ""}
                    {s.customer_name ? ` · ${s.customer_name}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button size="sm" variant="outline" onClick={saveDraft} disabled={saving}
                  data-testid="pb-save-draft">
            <Save className="w-4 h-4 mr-1" /> Save Draft
          </Button>
          <Button size="sm" variant="outline" onClick={clearDraft}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                  data-testid="pb-reset">
            <RotateCcw className="w-4 h-4 mr-1" /> Reset
          </Button>
          <Button size="sm" onClick={doPrint}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="pb-print">
            <Printer className="w-4 h-4 mr-1" /> Print / Save PDF
          </Button>
        </div>
      </div>

      {/* Printable form */}
      <div ref={printRef} className="pb-form-print bg-white shadow border border-slate-200 mx-auto p-6 max-w-[900px]" data-testid="pb-form-body">
        {/* Header */}
        <div className="text-center border-b-2 border-slate-800 pb-2 mb-3">
          <div className="text-xs italic text-slate-600">Form-II</div>
          <div className="text-[10px] italic text-slate-500">(see regulation 4)</div>
          <div className="text-lg font-bold tracking-wide">Postal Bill Of Export-II</div>
          <div className="text-[10px] italic text-slate-500">(To be submitted in duplicate)</div>
        </div>

        {/* Bill No, Date, FPO */}
        <table className="w-full text-xs border border-slate-800 border-collapse mb-2">
          <tbody>
            <tr>
              <td className="border border-slate-700 p-1 w-1/3 font-medium">Bill of Export No. &amp; date</td>
              <td className="border border-slate-700 p-1 w-1/3">
                <PbField value={form.bill_of_export_no} onChange={(v) => upd("bill_of_export_no", v)}
                   placeholder="No." data-testid="pb-bill-no" />
              </td>
              <td className="border border-slate-700 p-1 w-1/3">
                <PbField value={form.bill_of_export_date} onChange={(v) => upd("bill_of_export_date", v)}
                   placeholder="dd-mm-yyyy" data-testid="pb-bill-date" />
              </td>
            </tr>
            <tr>
              <td className="border border-slate-700 p-1 font-medium">Foreign Post office code</td>
              <td className="border border-slate-700 p-1" colSpan={2}>
                <PbField value={form.foreign_post_office_code} onChange={(v) => upd("foreign_post_office_code", v)}
                   placeholder="FPO code" data-testid="pb-fpo" />
              </td>
            </tr>
          </tbody>
        </table>

        {/* Exporter block (auto-filled from company) */}
        <div className="border border-slate-800 p-2 mb-2">
          <div className="text-[11px] font-bold uppercase text-slate-700 mb-1">Exporter details (auto-filled)</div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <Label className="text-[10px] text-slate-500">Name of Exporter</Label>
              <PbField value={form.exporter_name} onChange={(v) => upd("exporter_name", v)}
                 data-testid="pb-exporter-name"
                 className={selectedCompanyId && form.exporter_name ? "bg-purple-50/50 font-semibold" : ""} />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">IEC</Label>
              <PbField value={form.iec} onChange={(v) => upd("iec", v)}
                 data-testid="pb-iec"
                 className={selectedCompanyId && form.iec ? "bg-purple-50/50 font-semibold" : ""} />
            </div>
            <div className="col-span-2">
              <Label className="text-[10px] text-slate-500">Address of Exporter</Label>
              <PbField value={form.exporter_address} onChange={(v) => upd("exporter_address", v)}
                 multiline data-testid="pb-exporter-address"
                 className={selectedCompanyId && form.exporter_address ? "bg-purple-50/50 font-semibold" : ""} />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">State code</Label>
              <PbField value={form.state_code} onChange={(v) => upd("state_code", v)}
                 data-testid="pb-state-code"
                 className={selectedCompanyId && form.state_code ? "bg-purple-50/50 font-semibold" : ""} />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">GSTIN or as Applicable</Label>
              <PbField value={form.gstin} onChange={(v) => upd("gstin", v)}
                 data-testid="pb-gstin"
                 className={selectedCompanyId && form.gstin ? "bg-purple-50/50 font-semibold" : ""} />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">AD code (if Applicable)</Label>
              <PbField value={form.ad_code} onChange={(v) => upd("ad_code", v)} data-testid="pb-ad-code" />
            </div>
          </div>
        </div>

        {/* Customs Broker */}
        <div className="border border-slate-800 p-2 mb-2">
          <div className="text-[11px] font-bold uppercase text-slate-700 mb-1">Details Of Customs Broker</div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <Label className="text-[10px] text-slate-500">Licence No.</Label>
              <PbField value={form.broker_licence} onChange={(v) => upd("broker_licence", v)}
                 data-testid="pb-broker-licence" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Name &amp; Address</Label>
              <PbField value={form.broker_name_address} onChange={(v) => upd("broker_name_address", v)}
                 data-testid="pb-broker-name-address" />
            </div>
          </div>
        </div>

        {/* Declaration */}
        <table className="w-full text-xs border border-slate-800 border-collapse mb-2">
          <thead>
            <tr className="bg-slate-100">
              <th className="border border-slate-700 p-1 text-center w-8">#</th>
              <th className="border border-slate-700 p-1 text-left">Declaration</th>
              <th className="border border-slate-700 p-1 text-center w-24">Yes/No</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-slate-700 p-1 text-center">1</td>
              <td className="border border-slate-700 p-1">
                We declare that we intend to claim rewards under Merchandise Exports from India Scheme (MEIS) (for export through Chennai / Mumbai / Delhi FPO only).
              </td>
              <td className="border border-slate-700 p-1">
                <PbField value={form.decl_meis} onChange={(v) => upd("decl_meis", v)}
                   placeholder="Yes/No" data-testid="pb-decl-meis" />
              </td>
            </tr>
            <tr>
              <td className="border border-slate-700 p-1 text-center">2</td>
              <td className="border border-slate-700 p-1">
                We declare that we intend to zero rate exports under Section 16 of IGST Act.
              </td>
              <td className="border border-slate-700 p-1">
                <PbField value={form.decl_zero_rate} onChange={(v) => upd("decl_zero_rate", v)}
                   placeholder="Yes/No" data-testid="pb-decl-zero" />
              </td>
            </tr>
            <tr>
              <td className="border border-slate-700 p-1 text-center">3</td>
              <td className="border border-slate-700 p-1">
                We declare that the goods are exempted under CGST/SGST/UTGST/IGST Acts.
              </td>
              <td className="border border-slate-700 p-1">
                <PbField value={form.decl_exempted} onChange={(v) => upd("decl_exempted", v)}
                   placeholder="Yes/No" data-testid="pb-decl-exempt" />
              </td>
            </tr>
          </tbody>
        </table>
        <p className="text-[11px] italic text-slate-700 mb-2">
          We hereby declare that the contents of this postal bill of export are true and correct in every respect.
        </p>

        {/* Consignee */}
        <div className="border border-slate-800 p-2 mb-2">
          <div className="text-[11px] font-bold uppercase text-slate-700 mb-1">Buyer Name &amp; Address (Consignee Details)</div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="col-span-2">
              <Label className="text-[10px] text-slate-500">Name &amp; Address</Label>
              <PbField value={form.consignee_name_address} onChange={(v) => upd("consignee_name_address", v)}
                 multiline data-testid="pb-consignee-name" />
            </div>
            <div>
              <Label className="text-[10px] text-slate-500">Country of destination</Label>
              <PbField value={form.country_of_destination} onChange={(v) => upd("country_of_destination", v)}
                 data-testid="pb-country" />
            </div>
          </div>
        </div>

        {/* Details of parcel table */}
        <div className="text-[11px] font-bold uppercase text-slate-700 mb-1">Details of parcel</div>
        <table className="w-full text-[10px] border border-slate-800 border-collapse mb-3">
          <thead className="bg-slate-100">
            <tr>
              <th className="border border-slate-700 p-1">Description</th>
              <th className="border border-slate-700 p-1">HSN</th>
              <th className="border border-slate-700 p-1">Qty</th>
              <th className="border border-slate-700 p-1">Unit</th>
              <th className="border border-slate-700 p-1">Invoice No. &amp; Date</th>
              <th className="border border-slate-700 p-1">Weight</th>
              <th className="border border-slate-700 p-1">Postal Tracking No.</th>
              <th className="border border-slate-700 p-1" colSpan={4}>Assessable value u/s 14 Customs Act</th>
            </tr>
            <tr>
              <th className="border border-slate-700 p-1" colSpan={7}></th>
              <th className="border border-slate-700 p-1">FOB Value</th>
              <th className="border border-slate-700 p-1">Currency</th>
              <th className="border border-slate-700 p-1">Ex. rate</th>
              <th className="border border-slate-700 p-1">Amount (INR)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-slate-700 p-1"><PbField value={form.parcel_description} onChange={(v) => upd("parcel_description", v)} data-testid="pb-parcel-desc" /></td>
              <td className="border border-slate-700 p-1"><PbField value={form.parcel_hsn} onChange={(v) => upd("parcel_hsn", v)} data-testid="pb-parcel-hsn" /></td>
              <td className="border border-slate-700 p-1"><PbField value={form.parcel_qty} onChange={(v) => upd("parcel_qty", v)} data-testid="pb-parcel-qty" /></td>
              <td className="border border-slate-700 p-1"><PbField value={form.parcel_unit} onChange={(v) => upd("parcel_unit", v)} data-testid="pb-parcel-unit" /></td>
              <td className="border border-slate-700 p-1">
                <PbField value={form.parcel_invoice_no} onChange={(v) => upd("parcel_invoice_no", v)} placeholder="No." data-testid="pb-parcel-inv-no" />
                <PbField value={form.parcel_invoice_date} onChange={(v) => upd("parcel_invoice_date", v)} placeholder="Date" data-testid="pb-parcel-inv-date" />
              </td>
              <td className="border border-slate-700 p-1"><PbField value={form.parcel_weight} onChange={(v) => upd("parcel_weight", v)} data-testid="pb-parcel-weight" /></td>
              <td className="border border-slate-700 p-1"><PbField value={form.parcel_tracking_no} onChange={(v) => upd("parcel_tracking_no", v)} data-testid="pb-parcel-track" /></td>
              <td className="border border-slate-700 p-1"><PbField value={form.parcel_fob} onChange={(v) => upd("parcel_fob", v)} data-testid="pb-parcel-fob" /></td>
              <td className="border border-slate-700 p-1"><PbField value={form.parcel_currency} onChange={(v) => upd("parcel_currency", v)} data-testid="pb-parcel-cur" /></td>
              <td className="border border-slate-700 p-1"><PbField value={form.parcel_exchange_rate} onChange={(v) => upd("parcel_exchange_rate", v)} data-testid="pb-parcel-rate" /></td>
              <td className="border border-slate-700 p-1"><PbField value={form.parcel_amount_inr} onChange={(v) => upd("parcel_amount_inr", v)} data-testid="pb-parcel-inr" /></td>
            </tr>
          </tbody>
        </table>

        {/* Tax Invoice / Duty */}
        <div className="text-[11px] font-bold uppercase text-slate-700 mb-1">Details of Tax Invoice (whichever applicable)</div>
        <table className="w-full text-[10px] border border-slate-800 border-collapse mb-3">
          <thead className="bg-slate-100">
            <tr>
              <th className="border border-slate-700 p-1">H.S.N. Code</th>
              <th className="border border-slate-700 p-1">Invoice No. &amp; Date</th>
              <th className="border border-slate-700 p-1">SI No of item</th>
              <th className="border border-slate-700 p-1">Value in $</th>
              <th className="border border-slate-700 p-1" colSpan={2}>Export duty</th>
              <th className="border border-slate-700 p-1" colSpan={2}>Cess</th>
              <th className="border border-slate-700 p-1" colSpan={2}>IGST (if applicable)</th>
              <th className="border border-slate-700 p-1" colSpan={2}>Compensation cess</th>
              <th className="border border-slate-700 p-1">LUT/Bond</th>
              <th className="border border-slate-700 p-1" colSpan={2}>Total</th>
            </tr>
            <tr>
              <th className="border border-slate-700 p-1" colSpan={4}></th>
              <th className="border border-slate-700 p-1">Rate</th>
              <th className="border border-slate-700 p-1">Amount</th>
              <th className="border border-slate-700 p-1">Rate</th>
              <th className="border border-slate-700 p-1">Amount</th>
              <th className="border border-slate-700 p-1">Rate</th>
              <th className="border border-slate-700 p-1">Amount</th>
              <th className="border border-slate-700 p-1">Rate</th>
              <th className="border border-slate-700 p-1">Amount</th>
              <th className="border border-slate-700 p-1"></th>
              <th className="border border-slate-700 p-1">Duty</th>
              <th className="border border-slate-700 p-1">Cess</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-slate-700 p-1"><PbField value={form.tax_hsn} onChange={(v) => upd("tax_hsn", v)} data-testid="pb-tax-hsn" /></td>
              <td className="border border-slate-700 p-1"><PbField value={form.tax_invoice_no_date} onChange={(v) => upd("tax_invoice_no_date", v)} data-testid="pb-tax-inv" /></td>
              <td className="border border-slate-700 p-1"><PbField value={form.tax_si_no} onChange={(v) => upd("tax_si_no", v)} data-testid="pb-tax-sino" /></td>
              <td className="border border-slate-700 p-1"><PbField value={form.tax_value_dollars} onChange={(v) => upd("tax_value_dollars", v)} data-testid="pb-tax-value" /></td>
              <td className="border border-slate-700 p-1"><PbField value={form.duty_export_rate} onChange={(v) => upd("duty_export_rate", v)} /></td>
              <td className="border border-slate-700 p-1"><PbField value={form.duty_export_amount} onChange={(v) => upd("duty_export_amount", v)} /></td>
              <td className="border border-slate-700 p-1"><PbField value={form.duty_cess_rate} onChange={(v) => upd("duty_cess_rate", v)} /></td>
              <td className="border border-slate-700 p-1"><PbField value={form.duty_cess_amount} onChange={(v) => upd("duty_cess_amount", v)} /></td>
              <td className="border border-slate-700 p-1"><PbField value={form.duty_igst_rate} onChange={(v) => upd("duty_igst_rate", v)} /></td>
              <td className="border border-slate-700 p-1"><PbField value={form.duty_igst_amount} onChange={(v) => upd("duty_igst_amount", v)} /></td>
              <td className="border border-slate-700 p-1"><PbField value={form.duty_compensation_rate} onChange={(v) => upd("duty_compensation_rate", v)} /></td>
              <td className="border border-slate-700 p-1"><PbField value={form.duty_compensation_amount} onChange={(v) => upd("duty_compensation_amount", v)} /></td>
              <td className="border border-slate-700 p-1"><PbField value={form.lut_bond} onChange={(v) => upd("lut_bond", v)} data-testid="pb-lut" /></td>
              <td className="border border-slate-700 p-1"><PbField value={form.total_duty} onChange={(v) => upd("total_duty", v)} /></td>
              <td className="border border-slate-700 p-1"><PbField value={form.total_cess} onChange={(v) => upd("total_cess", v)} /></td>
            </tr>
          </tbody>
        </table>

        {/* Signature area */}
        <div className="grid grid-cols-2 gap-6 mt-8 text-xs">
          <div className="border-t border-slate-700 pt-1 text-center">
            (Signature of the Exporter / Authorised agent)
          </div>
          <div className="border-t border-slate-700 pt-1 text-center">
            Let Export Order: Signature of officer of Customs along with stamp and date.
          </div>
        </div>

        <div className="mt-6 text-[10px] italic text-slate-600 border-t border-slate-300 pt-2">
          Examination order and report:
          <div className="h-16"></div>
        </div>
      </div>

      {/* Print stylesheet — matches A4 layout */}
      <style>{`
        @media print {
          body { background: white !important; }
          .print\\:hidden { display: none !important; }
          .pb-form-print {
            border: none !important;
            box-shadow: none !important;
            margin: 0 !important;
            max-width: none !important;
            padding: 12mm !important;
          }
          .pb-form-print input,
          .pb-form-print textarea {
            border: none !important;
            background: transparent !important;
            color: black !important;
          }
          @page { size: A4; margin: 8mm; }
        }
      `}</style>
    </div>
  );
};

export default PbFormPanel;
